import { MarketplaceCode } from '@backmarket/http-api'

import { ExtendedCurrency } from './BusinessDetails.types'

/**
 * Validation regex patterns for KYB Business Details form
 *
 * @see https://backmarket.atlassian.net/wiki/spaces/MCC/pages/5102371391/tech+specs+KYB+form+validation+rules
 */
export const VALIDATION_REGEX = {
  /**
   * Company Name validation (Legal Name & Trade Name)
   *
   * Allows:
   * - Letters (a-z, A-Z) including accented characters (À-ÿ)
   * - Numbers (0-9)
   * - Spaces
   * - Special characters: - . , ' & + / : ; !
   * - Length: 1-90 characters
   *
   * Unicode ranges:
   * - \u00C0-\u00FF: Latin-1 Supplement (À, Á, Â, etc.)
   * - \u0100-\u017F: Latin Extended-A (Ā, Ă, Ą, etc.)
   * - \u0180-\u024F: Latin Extended-B (ƀ, Ɓ, Ƃ, etc.)
   */
  COMPANY_NAME:
    /^[\sa-zA-Z0-9\-.,'&+/:;!\u00C0-\u00FF\u0100-\u017F\u0180-\u024F]{1,90}$/,

  /**
   * Registration Number validation (EIN, etc.)
   *
   * Allows:
   * - Letters (a-z, A-Z)
   * - Numbers (0-9)
   * - Spaces
   * - Parentheses: ( )
   * - Plus sign: +
   * - Minus/hyphen: -
   * - Period: .
   * - Length: 1-50 characters
   */
  REGISTRATION_NUMBER: /^[a-zA-Z0-9\s()+\-.]{1,50}$/,
} as const

export const CURRENCIES = {
  [MarketplaceCode.NA]: [ExtendedCurrency.CAD, ExtendedCurrency.USD],

  [MarketplaceCode.EU]: [
    ExtendedCurrency.DKK,
    ExtendedCurrency.EUR,
    ExtendedCurrency.HUF,
    ExtendedCurrency.PLN,
    ExtendedCurrency.RON,
    ExtendedCurrency.SEK,
    ExtendedCurrency.BGN,
    ExtendedCurrency.CZK,
    ExtendedCurrency.GBP,
  ],

  [MarketplaceCode.AP]: [ExtendedCurrency.JPY, ExtendedCurrency.AUD],
}

export const COUNTRY_CODES = [
  'AF',
  'AX',
  'AL',
  'DZ',
  'AS',
  'AD',
  'AO',
  'AI',
  'AQ',
  'AG',
  'AR',
  'AM',
  'AW',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BE',
  'BZ',
  'BJ',
  'BM',
  'BT',
  'BO',
  'BA',
  'BW',
  'BV',
  'BR',
  'IO',
  'VG',
  'BN',
  'BG',
  'BF',
  'BI',
  'KH',
  'CM',
  'CA',
  'CV',
  'BQ',
  'KY',
  'CF',
  'TD',
  'CL',
  'CN',
  'CX',
  'CC',
  'CO',
  'KM',
  'CG',
  'CD',
  'CK',
  'CR',
  'CI',
  'HR',
  'CW',
  'AN',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'SZ',
  'ET',
  'FK',
  'FO',
  'FJ',
  'FI',
  'FR',
  'GF',
  'PF',
  'TF',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GI',
  'GR',
  'GL',
  'GD',
  'GP',
  'GU',
  'GT',
  'GG',
  'GN',
  'GW',
  'GY',
  'HT',
  'HM',
  'HN',
  'HK',
  'HU',
  'IS',
  'IN',
  'ID',
  'IE',
  'IM',
  'IL',
  'IT',
  'JM',
  'JP',
  'JE',
  'JO',
  'KZ',
  'KE',
  'KI',
  'XK',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MO',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MQ',
  'MR',
  'MU',
  'YT',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MS',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NL',
  'NC',
  'NZ',
  'NI',
  'NE',
  'NG',
  'NU',
  'NF',
  'MP',
  'MK',
  'NO',
  'OM',
  'PK',
  'PW',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PN',
  'PL',
  'PT',
  'PR',
  'QA',
  'RE',
  'RO',
  'RU',
  'RW',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SX',
  'SK',
  'SI',
  'SB',
  'ZA',
  'GS',
  'KR',
  'SS',
  'ES',
  'LK',
  'BL',
  'SH',
  'KN',
  'LC',
  'MF',
  'PM',
  'VC',
  'SR',
  'SJ',
  'SE',
  'CH',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TK',
  'TO',
  'TT',
  'TN',
  'TM',
  'TC',
  'TV',
  'UM',
  'VI',
  'UG',
  'UA',
  'AE',
  'GB',
  'US',
  'UY',
  'UZ',
  'VU',
  'VA',
  'VE',
  'VN',
  'WF',
  'EH',
  'YE',
  'ZM',
  'ZW',
] as const
